import React, { memo, useCallback, useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router';

import { Box } from '@mui/material';
import { getBrokerConditionSchema } from 'app/pages/loanApplication/tabs/underwriting/tabs/loanConditions/AddConditionsForm.utils';
import { onAutocompleteChangeInForm } from 'app/shared/components/form/Autocomplete';
import _ from 'lodash';
import PropTypes from 'prop-types';

import useForm from 'hooks/useForm';
import useLoanId from 'hooks/useLoanId';

import { getLoanSpecificForms } from 'store/forms/actions';
import { openDialogAction, showMessageAction } from 'store/global/actions';
import {
  addInternalConditionAction,
  getLoanConditionByLoanIdAction,
  getLoanApplicationTasksAction,
} from 'store/loanApplications/actions';
import {
  selectFormHiddenFieldsMap,
  selectFormLockedFieldsMap,
  selectFormLockedPage,
} from 'store/loanApplications/selectors';
import { selectTaskTypeDynamicField, selectTaskTypesOptions } from 'store/system/selectors';
import { selectPriorTo, selectSlaGroupsOptions } from 'store/underwriting/selectors';

import { initialInternalFormValues } from '../../../AddBrokerConditionForm/AddBrokerConditionForm.constants';
import {
  applyUploadedFormToTask,
  getCreateFormDialogProps,
  getFormViewerDialogProps,
  prepareInternalConditionRequest,
} from '../../../AddBrokerConditionForm/AddBrokerConditionForm.utils';
import AdhocConditionFields from '../../../components/AdhocConditionFields';

import { FORM_NAME } from './AddAdhocConditionContent.constants';
import { contentWrapperStyles } from './AddAdhocConditionContent.styles';

const AddAdhocConditionContent = ({
  additionalProps: { loanId: loanIdProp, initialForm, customOnAssignFormClick = _.noop },
  setIsFormChanged,
  setOnSave,
  onClose,
}) => {
  const dispatch = useDispatch();
  const { applicationId } = useParams();
  const loanId = useLoanId() || loanIdProp;

  const taskTypeOptions = useSelector(selectTaskTypesOptions);
  const priorToOptions = useSelector(selectPriorTo);
  const slaGroupsOptions = useSelector(selectSlaGroupsOptions);
  const taskTypeDynamicFieldOptions = useSelector(selectTaskTypeDynamicField);
  const lockedFieldsMap = useSelector(selectFormLockedFieldsMap(FORM_NAME));
  const hiddenFieldsMap = useSelector(selectFormHiddenFieldsMap(FORM_NAME));
  const isPageLocked = useSelector(selectFormLockedPage(FORM_NAME));

  const {
    form,
    isFormChanged,
    setInForm,
    validationErrors,
    setSchema,
    handleChange,
    validateForm,
    validateField,
  } = useForm(initialForm || initialInternalFormValues);

  const onAutocompleteChange = useMemo(
    () => onAutocompleteChangeInForm({ setInForm, validateField }),
    [setInForm, validateField],
  );

  const onCreateNewForm = useCallback(
    ({ taskIndex }) => {
      const abortController = new AbortController();

      const onFinishFormUploading = uploadedForm => {
        dispatch(getLoanSpecificForms({ loanId, applicationId }))
          .catch(Error)
          .finally(() => {
            const newForm = applyUploadedFormToTask({ form, taskIndex, uploadedForm });
            const onAssignClick = () => customOnAssignFormClick(newForm);

            dispatch(openDialogAction(getFormViewerDialogProps({ uploadedForm, onAssignClick })));
          });
      };

      dispatch(
        openDialogAction(
          getCreateFormDialogProps({ abortController, applicationId, onFinishFormUploading }),
        ),
      );
    },
    [applicationId, customOnAssignFormClick, dispatch, form, loanId],
  );

  const onSubmit = useCallback(async () => {
    if (!(await validateForm())) {
      return;
    }

    const request = prepareInternalConditionRequest({
      form,
      applicationId,
      loanId,
      taskTypeDynamicFieldOptions,
    });

    return dispatch(addInternalConditionAction(request))
      .then(() =>
        Promise.all([
          dispatch(
            getLoanConditionByLoanIdAction({ loanId, partialLoad: true, withDeleted: true }),
          ),
          dispatch(getLoanApplicationTasksAction({ applicationId })),
        ]),
      )
      .then(() => {
        dispatch(
          showMessageAction({ message: 'Condition successfully added.', variant: 'success' }),
        );
        onClose();
      })
      .catch(Error);
  }, [applicationId, dispatch, form, loanId, onClose, taskTypeDynamicFieldOptions, validateForm]);

  useEffect(() => {
    setSchema(
      getBrokerConditionSchema({
        conditionTaskTypes: form.conditionTaskTypes,
        taskTypeDynamicFieldOptions,
        isInternal: true,
      }),
    );
  }, [setSchema, form.conditionTaskTypes, taskTypeDynamicFieldOptions]);

  useEffect(() => {
    setIsFormChanged(isFormChanged);
  }, [isFormChanged, setIsFormChanged]);

  useEffect(() => {
    setOnSave(() => onSubmit);
  }, [onSubmit, setOnSave]);

  return (
    <Box sx={contentWrapperStyles}>
      <AdhocConditionFields
        isInternal
        isTasksEnabled
        form={form}
        formName={FORM_NAME}
        handleChange={handleChange}
        hiddenFieldsMap={hiddenFieldsMap}
        isDisabledByLock={isPageLocked}
        lockedFieldsMap={lockedFieldsMap}
        priorToOptions={priorToOptions}
        setInForm={setInForm}
        slaGroupsOptions={slaGroupsOptions}
        taskTypeOptions={taskTypeOptions}
        validateField={validateField}
        validationErrors={validationErrors}
        onAutocompleteChange={onAutocompleteChange}
        onCreateNewForm={onCreateNewForm}
      />
    </Box>
  );
};

AddAdhocConditionContent.propTypes = {
  additionalProps: PropTypes.shape({
    loanId: PropTypes.string,
    initialForm: PropTypes.object,
    customOnAssignFormClick: PropTypes.func,
  }).isRequired,
  setIsFormChanged: PropTypes.func.isRequired,
  setOnSave: PropTypes.func.isRequired,
  onClose: PropTypes.func.isRequired,
};

export default memo(AddAdhocConditionContent);
